import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaChevronRight } from 'react-icons/fa'
import { FaDownLeftAndUpRightToCenter, FaUpRightAndDownLeftFromCenter } from 'react-icons/fa6'
import { Link, useHistory } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { BusinessLogo, Button, SearchInput, Shimmer } from '@components'
import {
  useBusinessOverview,
  useCurrentUser,
  useOverviewColumnOptions,
  useOverviewFilterCounts
} from '@query'
import { useScreen } from '@utils'
import {
  COLUMNS,
  OPTIONAL_COLUMNS,
  visibleColumns
} from '@pages/BusinessesPage/components/table/columns'
import { ColumnCell } from '@pages/BusinessesPage/components/table/PrimaryCells'
import {
  businessSubtitle,
  initialsOf,
  rowTint,
  urgencyReason
} from '@pages/BusinessesPage/components/table/format'
import { useColumnPrefs } from '@pages/BusinessesPage/components/table/useColumnPrefs'
import { TablePagination } from '@pages/BusinessesPage/components/table/TablePagination'
import { IconButton } from './BusinessMenu.styled'

// The page size adapts to the space below the trigger: at least MIN rows
// on any desktop screen, up to MAX on tall ones. Small fixed pages either
// way, fetched exactly like the businesses page paginates, so browsing
// stays cheap on the DB. Mobile keeps its original fixed page.
const MIN_PAGE_SIZE = 3
const MAX_PAGE_SIZE = 8
const MOBILE_PAGE_SIZE = 5

// Measured chrome around the list (top bar 56 + search 48 + column header
// 32 + pagination 59) and one row's height; pinned by the paddings and
// font sizes below, so the fit stays exact until those change. The bottom
// gap keeps the panel from kissing the viewport edge at full page size.
const LIST_CHROME_PX = 195
const PANEL_BOTTOM_GAP_PX = 48
const ROW_PX = 54

export const pageSizeForHeight = (maxHeight: number): number =>
  Math.max(
    MIN_PAGE_SIZE,
    Math.min(MAX_PAGE_SIZE, Math.floor((maxHeight - LIST_CHROME_PX - PANEL_BOTTOM_GAP_PX) / ROW_PX))
  )

const Wrap = styled.div``

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.md}rem
    ${({ theme }) => theme.spacing.sm}rem;
`

const CountLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-weight: 600;
  white-space: nowrap;
`

const SearchRow = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.md}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSoft};
`

const ListScroll = styled.div`
  overflow-x: auto;
`

const HeaderRow = styled.div<{ $template: string; $minWidth: number }>`
  display: grid;
  grid-template-columns: ${({ $template }) => $template};
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-width: ${({ $minWidth }) => $minWidth}px;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSoft};
  background: ${({ theme }) => theme.colors.defaultHover};
`

const HeaderLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.px105}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.iconGray};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
`

const Row = styled(Link)<{ $template: string; $minWidth: number; $bg: string; $hoverBg: string }>`
  display: grid;
  grid-template-columns: ${({ $template }) => $template};
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-width: ${({ $minWidth }) => $minWidth}px;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSoft};
  position: relative;
  cursor: pointer;
  background: ${({ $bg }) => $bg};
  text-decoration: none;
  color: inherit;

  &:hover,
  &:focus-visible {
    background: ${({ $hoverBg }) => $hoverBg};
    text-decoration: none;
  }
`

const Accent = styled.span<{ $color: string }>`
  position: absolute;
  left: 0;
  top: ${({ theme }) => theme.spacing.xs}rem;
  bottom: ${({ theme }) => theme.spacing.xs}rem;
  width: 3px;
  border-radius: 0 4px 4px 0;
  background: ${({ $color }) => $color};
`

const BusinessCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
`

const BusinessName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.menuBg};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BusinessSub = styled.div`
  font-size: ${({ theme }) => theme.fontSize.px105}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-weight: 500;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EmptyText = styled.div`
  padding: ${({ theme }) => theme.spacing.xl}rem ${({ theme }) => theme.spacing.lg}rem;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.iconGray};
`

// First-load placeholders sized like real rows, so the panel opens at its
// final height instead of growing when the data lands.
const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSoft};
`

interface Props {
  search: string
  onSearchChange: (value: string) => void
  expanded: boolean
  onToggleExpanded: () => void
  onRequestClose: () => void
  // Vertical space available to the whole panel; drives the page size.
  maxHeight: number
}

// The business-menu list: the business column plus the user's saved table
// columns (first three when compact, all of them when expanded; business
// only on small screens), paginated in small fixed pages.
export const CompactList: React.FC<Props> = ({
  search,
  onSearchChange,
  expanded,
  onToggleExpanded,
  onRequestClose,
  maxHeight
}) => {
  const [t] = useTranslation()
  const theme = useTheme()
  const history = useHistory()
  const { isMobile } = useScreen()
  const { data: user } = useCurrentUser()
  const isAgencyUser = !!user?.is_agency_user
  const { enabledColumns } = useColumnPrefs(user)
  // Same permission-filtered options as the businesses table: saved columns
  // the user may no longer see must not render as dead cells here either.
  const { data: columnOptions, isError: columnOptionsError } = useOverviewColumnOptions()
  const availableColumns = columnOptions ? new Set(columnOptions.map(option => option.value)) : null
  const savedColumns = visibleColumns(enabledColumns, isAgencyUser, availableColumns)
  const columns = isMobile ? [] : expanded ? savedColumns : savedColumns.slice(0, 3)

  const pageSize = isMobile ? MOBILE_PAGE_SIZE : pageSizeForHeight(maxHeight)

  const [page, setPage] = useState(1)

  // Reset pagination atomically with a search change; a page-size change
  // (window resized) re-chunks the list, so it starts over too.
  const [prevSearch, setPrevSearch] = useState(search)
  const [prevPageSize, setPrevPageSize] = useState(pageSize)
  if (search !== prevSearch || pageSize !== prevPageSize) {
    setPrevSearch(search)
    setPrevPageSize(pageSize)
    setPage(1)
  }

  // Every saved optional column is requested up front, from the saved
  // preferences ALONE: the query fires in parallel with the options call
  // (the backend drops or masks anything unpermitted), the key stays
  // stable when the options arrive, and expanding to the full column set
  // needs no second fetch.
  const requestedColumns = isMobile
    ? ''
    : visibleColumns(enabledColumns, isAgencyUser)
        .filter(key => OPTIONAL_COLUMNS.includes(key))
        .join(',')
  const columnsReady = columnOptions !== undefined || columnOptionsError
  const { data, isLoading, isError } = useBusinessOverview(
    {
      search: search || undefined,
      page,
      page_size: pageSize,
      ordering: 'deadline',
      columns: requestedColumns || undefined
    },
    { enabled: user !== undefined }
  )
  const { data: filterCounts } = useOverviewFilterCounts()

  const rows = data?.results || []
  const filteredTotal = data?.count ?? 0
  const portfolioTotal = filterCounts?.total ?? filteredTotal
  const pageCount = Math.max(1, Math.ceil(filteredTotal / pageSize))
  // The gated query reports NOT loading while the user profile or column
  // options are in flight (react-query v5); all phases are "still loading"
  // for the UI.
  const pending = user === undefined || !columnsReady || (isLoading && !data)

  const template = ['minmax(180px, 2fr)', ...columns.map(key => COLUMNS[key].fr), '18px'].join(' ')
  const minWidth =
    180 + columns.reduce((sum, key) => sum + COLUMNS[key].minPx, 0) + 18 + 14 * (columns.length + 1)

  return (
    <Wrap>
      <TopBar>
        <Button
          type="button"
          size="sm"
          intent="primary"
          isSecondary
          icon={<FaArrowLeft />}
          onClick={() => history.push('/start')}
        >
          {t('businessesPage.menu.backToBusinesses')}
        </Button>
        <span style={{ flex: 1 }} />
        <CountLabel>
          {search
            ? t('businessesPage.menu.countOf', { shown: filteredTotal, total: portfolioTotal })
            : t('businessesPage.table.businessesCount', { count: portfolioTotal })}
        </CountLabel>
        <IconButton
          type="button"
          title={t(expanded ? 'businessesPage.menu.minimize' : 'businessesPage.menu.expand')}
          onClick={onToggleExpanded}
        >
          {expanded ? (
            <FaDownLeftAndUpRightToCenter style={{ fontSize: `${theme.fontSize.xs}rem` }} />
          ) : (
            <FaUpRightAndDownLeftFromCenter style={{ fontSize: `${theme.fontSize.xs}rem` }} />
          )}
        </IconButton>
      </TopBar>

      <SearchRow>
        <SearchInput
          autoFocus
          initialValue={search}
          placeholder={t('businessesPage.searchPlaceholder')}
          onSearch={onSearchChange}
          delay={200}
        />
      </SearchRow>

      <ListScroll>
        <HeaderRow $template={template} $minWidth={minWidth}>
          <HeaderLabel>{t('businessesPage.table.columnDefs.business.header')}</HeaderLabel>
          {columns.map(key => (
            <HeaderLabel key={key}>
              {t(`businessesPage.table.columnDefs.${key}.header`)}
            </HeaderLabel>
          ))}
          <div />
        </HeaderRow>

        {/* A modest fixed skeleton count: the panel collapses to the real
            row count when the data lands, so guessing the page size here
            would just exaggerate the jump for small portfolios. */}
        {pending &&
          Array.from({ length: MIN_PAGE_SIZE }, (_, index) => (
            <SkeletonRow key={index}>
              <Shimmer width={34} height={34} borderRadius={`${theme.borderRadius.sm}rem`} />
              <Shimmer
                width={150 + (index % 3) * 24}
                height={13}
                borderRadius={`${theme.borderRadius.sm}rem`}
              />
            </SkeletonRow>
          ))}

        {rows.map(row => {
          const { accent, background, hoverBackground } = rowTint(row, theme)

          return (
            <Row
              key={row.id}
              to={`/${row.slug}`}
              onClick={onRequestClose}
              title={urgencyReason(row, t) || undefined}
              $template={template}
              $minWidth={minWidth}
              $bg={background}
              $hoverBg={hoverBackground}
            >
              <Accent $color={accent} />
              <BusinessCell>
                <BusinessLogo
                  src={row.logo || undefined}
                  name={row.name}
                  size={34}
                  borderRadius={`${theme.borderRadius.sm}rem`}
                  getInitials={initialsOf}
                />
                <div style={{ minWidth: 0 }}>
                  <BusinessName>{row.name}</BusinessName>
                  <BusinessSub>{businessSubtitle(row, t)}</BusinessSub>
                </div>
              </BusinessCell>
              {columns.map(column => (
                <ColumnCell key={column} column={column} row={row} />
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <FaChevronRight
                  style={{ fontSize: `${theme.fontSize.xs}rem`, color: theme.colors.borderStrong }}
                />
              </div>
            </Row>
          )
        })}
      </ListScroll>

      {isError && <EmptyText>{t('businessesPage.table.loadError')}</EmptyText>}

      {rows.length === 0 && !pending && !isError && (
        <EmptyText>
          {search
            ? t('businessesPage.menu.noMatches', { search })
            : t('businessesPage.table.emptyFiltered')}
        </EmptyText>
      )}

      {data && (
        <TablePagination
          page={page}
          pageCount={pageCount}
          shownCount={rows.length}
          totalCount={filteredTotal}
          onPage={setPage}
        />
      )}
    </Wrap>
  )
}
